import { useState, useEffect, useRef } from 'react';
import { Pdf } from '@/components';
import { useFilterContext } from '@/context/FilterContext';
import { useProjectContext } from '@/context/ProjectContext';
import { useMapContext } from '@/context/MapContext';
import { pdf } from '@react-pdf/renderer';
import useUser from '@/api/queries/useUser';
import useSalesRef from '@/api/mutations/useSalesRef';
import useMarket from '@/api/queries/useMarket';
import useAdsRef from '@/api/mutations/useAdsRef';
import useIndex from '@/api/queries/useIndex';
import { convertBoundsToSquareSides, calculateBounds, getDistance }
from '@/helpers';
import { getSoldApiParams, getAdsApiParams, getMarketApiParams } from '@/api/apiHelpers';
import useProject from '@/api/queries/useProject';
import ChartJsImage from '@/components/pdf/ChartJsImage';
import { getMapMarkerDataFromSold, filterLineChartDataBasedOnDateInterval } from '@/helpers';
import useBestIndex from '@/api/queries/useBestIndex';
import { LOADING_TYPE, DISTANCE_STEPS_ADS, DISTANCE_STEPS_SOLD } from "@/constants";
import {
    defaultColors,
    defaultFillOptions,
    defaultOptionsLine,
    defaultOptionsPie,
} from '@/components/pdf/components/defaultsForCharts';
import strings from '@/language/Swedish';
import {
    HomeTypeEnum,
    SearchParams
} from '@/enums';
import { useTableCalculations } from '@/context/hooks/useTableCalculations';

export default function PdfReportViewer({ props }: any) {
    const [loaded, setLoaded] = useState(false);
    const [soldStep, setSoldStep] = useState(0);
    const [adsStep, setAdsStep] = useState(0);
    const [isSoldFetchingDone, setIsSoldFetchingDone] = useState(false);
    const [isAdsFetchingDone, setIsAdsFetchingDone] = useState(false);
    const [projectDataLoaded, setProjectDataLoaded] = useState(false);
    const { project_id, type, sold, ads, market, priceDevelopment, comment, selected_columns_sold, selected_columns_ads, show_ref_price, limit } = props; //market_zoom
    const { project, updateSoldTableData, updateAdsTableData, setProject, setNewProject } = useProjectContext();
    const { currentCoordinates, soldMarkerObjects, setSoldMarkerObjects, setCoordinates, map } = useMapContext();
    const { data: userData, isLoading: isUserLoading, isError: isUserError } = useUser();
    const pdfContainerRef = useRef();
    const selectedReportItems = { sold: sold, ads: ads, market: market, priceDevelopment: priceDevelopment };
    const {
        soldData: { refData },
        marketData,
        soldData: soldFilterData,
        adsData: adsFilterData,
        marketData: marketFilterData,
        setSoldAndAdParam,
        setSoldParams,
        setAdsParams,
        setMarketParams
    } = useFilterContext();
    const {soldCalculations, adsCalculations} = useTableCalculations()

    const [marketImage, setMarketImage] = useState<string | null>(null);
    const [priceImage, setPriceImage] = useState<string | null>(null);

    const soldParams = getSoldApiParams(soldFilterData);
    const adsParams = getAdsApiParams(adsFilterData);
    const marketsParams = getMarketApiParams(marketFilterData);
    const { data: projectData } = useProject(project_id);
    const { mutate: updateAdsMutation, data: searchAdsData, isSuccess: adsLoaded } = useAdsRef();
    const { mutate: updateSoldMutation, data: searchSalesData, isSuccess: salesLoaded } = useSalesRef();
    const zoomLevel = map?.getZoom();
    const distance = getDistance(zoomLevel ?? 10);
    const newBounds = calculateBounds(currentCoordinates, distance);
    const marketBounds = convertBoundsToSquareSides(newBounds);
    const projectMarketBounds = {
        rt90x_max: projectData?.project?.ads_market_share_search_criteria?.rt90x_max,
        rt90x_min: projectData?.project?.ads_market_share_search_criteria?.rt90x_min,
        rt90y_max: projectData?.project?.ads_market_share_search_criteria?.rt90y_max,
        rt90y_min: projectData?.project?.ads_market_share_search_criteria?.rt90y_min
    };
    const bounds = projectMarketBounds?.rt90x_max ? projectMarketBounds : marketBounds;
    const { isSuccess: marketSuccess, data: useMarketData } = useMarket(
        market ? bounds : null,
        bounds?.rt90x_max ? marketsParams : null,
        project.marketPolygons
    );

    function fetchAds(step: number) {
        updateAdsMutation({
            coordinates: currentCoordinates,
            parameters: adsParams,
            adsTableIds: project.adsTableIds,
            polygonCoordinates: project.adsPolygons,
            distance: DISTANCE_STEPS_ADS[step]
        });
    }

    function fetchSold(step: number) {
        updateSoldMutation(
            {
                coordinates: currentCoordinates,
                parameters: soldParams,
                soldTableIds: project.soldTableIds,
                polygonCoordinates: project.soldPolygons,
                distance: DISTANCE_STEPS_SOLD[step]
            });
    }

    useEffect(() => {
        setSoldAndAdParam(SearchParams.filter, 'limit', limit.toString());
    }, []);

    useEffect(() => {
        if (searchAdsData && searchAdsData?.ads.length < limit && adsStep < 2) {
            const newAdsStep = adsStep + 1;
            setAdsStep(newAdsStep);
            fetchAds(newAdsStep);
        } else {
            setIsAdsFetchingDone(true);
        }
    }, [adsLoaded]);

    useEffect(() => {
        if (searchSalesData?.sales && searchSalesData?.sales?.length < limit && soldStep < 2) {
            const newSoldStep = soldStep + 1;
            setSoldStep(newSoldStep);
            fetchSold(newSoldStep);
        } else if (searchSalesData?.sales) {
            setIsSoldFetchingDone(true);
        }
    }, [searchSalesData]);


    useEffect(() => {
        if (useMarketData) {
            setProject((prevState) => ({ ...prevState, marketAds: useMarketData }));
        }
    }, [useMarketData]);


    // Create a local state to track when indexValues is ready for the useIndex query
    const [indexValuesReady, setIndexValuesReady] = useState(false);
    const { data: indexId } = useBestIndex(
        project?.indexValues?.home_type || HomeTypeEnum.apartment,
        (priceDevelopment || market) ? currentCoordinates : currentCoordinates
    );
    const {
        data: indexData,
        isSuccess: priceDevelopmentLoaded,
    } = useIndex(priceDevelopment && indexValuesReady ? project.indexValues : null);

    useEffect(() => {
        if (indexId && !indexValuesReady) {
            // const referenceDate = formatDateToString(
            //     subtractYearsFromDate(new Date(), 1)
            // );
            setProject((prevState) => ({
                ...prevState,
                indexValues: {
                    index_id: indexId.indexId,
                    reference_value: null,
                    reference_type: "Index",
                    referenceDate: new Date().toISOString()
                },
            }));

            // Signal that indexValues is now ready for the useIndex query
            setTimeout(() => setIndexValuesReady(true), 0);
        }
    }, [indexId]);


    useEffect(() => {
        const new_selected_columns_sold = project.soldTableColumns;
        const new_selected_columns_ads = project.adsTableColumns;
        if (selected_columns_sold) {
            const visible_columns = selected_columns_sold.split(",");
            new_selected_columns_sold.forEach((item) => {
                item.isVisible = visible_columns.includes(item.id);
            });
        }
        if (selected_columns_ads) {
            const visible_columns = selected_columns_ads.split(",");
            new_selected_columns_ads.forEach((item) => {
                item.isVisible = visible_columns.includes(item.id);
            });
        }
        if (selected_columns_sold || new_selected_columns_ads) {
            setProject((prevState) => ({ ...prevState, soldTableColumns: new_selected_columns_sold, adsTableColumns: new_selected_columns_ads }));
        }
    }, []);
    useEffect(() => {
        if (projectData) {
            const { coordinates, project,soldParams, adsParams,marketParams } =
                projectData;
            if (selectedReportItems.sold && soldParams) {
                setSoldParams(
                    soldParams.refData,
                    soldParams.weightData,
                    soldParams.filterData
                );
            }
            if (selectedReportItems.ads && adsParams) {
                setAdsParams(
                    adsParams.refData,
                    adsParams.weightData,
                    adsParams.filterData
                );
            }
            if (selectedReportItems.market && marketParams) {
                setMarketParams(marketParams.filterData);
            }
            setCoordinates(coordinates);
            setNewProject(project);
            setProjectDataLoaded(true);
        }
    }, [projectData]);

    useEffect(() => {
        if (projectDataLoaded && !salesLoaded && !adsLoaded && project.adsTableIds && project.soldTableIds) {
            fetchSold(0);
            fetchAds(0);
        }
    }, [projectDataLoaded, project.adsTableIds, project.soldTableIds]);

    useEffect(() => {
        if (searchSalesData?.sales && isSoldFetchingDone) {
            updateSoldTableData(searchSalesData);
            if (searchSalesData?.sales.length > 0) {
                const mapMarkerData = getMapMarkerDataFromSold(searchSalesData.sales);
                setSoldMarkerObjects(mapMarkerData);
            }
        }
    }, [searchSalesData, isSoldFetchingDone]);

    useEffect(() => {
        if (searchAdsData?.ads && isAdsFetchingDone) {
            updateAdsTableData(searchAdsData);
        }
    }, [searchAdsData, isAdsFetchingDone]);

    useEffect(() => {
        if (selectedReportItems?.market && project.marketAds && indexId && !marketImage) {

            const sortedData = project.marketAds.ads_market_shares.sort(
                (a, b) => b.market_share - a.market_share
            );
            const labels = sortedData.slice(0, 10).map(({ firm }) => firm);
            const dataPoints = sortedData
                .slice(0, 10)
                .map(({ market_share }) => market_share);

            const sumOfOthersData =
                sortedData.length > 10
                    ? project.marketAds.ads_market_shares
                        .slice(10)
                        .reduce((acc, item) => acc + item.market_share, 0)
                    : 0;

            const myChart = new ChartJsImage();
            myChart.setConfig({
                options: defaultOptionsPie,
                type: 'pie',
                data: {
                    labels: [
                        ...labels,
                        ...(sumOfOthersData !== 0 ? [strings.market.othersLabel] : []),
                    ],
                    datasets: [
                        {
                            label: 'chartLabel',
                            data: [
                                ...dataPoints,
                                ...(sumOfOthersData !== 0 ? [sumOfOthersData] : []),
                            ],
                            borderWidth: 1,
                            backgroundColor: [
                                ...defaultColors.map((color) => `rgba(${color})`),
                                'rgba(148, 163, 184, 0.45)',
                            ],
                        },
                    ],
                },
            });

            myChart
                .setWidth(500)
                .setHeight(300)
                .toDataUrl()
                .then((data) => setMarketImage(data));
        }
    }, [project]);

    useEffect(() => {
        if (!project?.indexData) {
            setProject((prevState) => ({
                ...prevState,
                indexData: indexData,
            }));
        }
    }, [indexData]);

    useEffect(() => {
        if (selectedReportItems?.priceDevelopment && project.indexData?.values) {
            const data = filterLineChartDataBasedOnDateInterval(project.indexData.values, project.index_normalized_fields.reference_date_from, project.index_normalized_fields.reference_date_to)
            const dataPoints = data.map((item) => item.value);
            const getMaxValueYaxis = () => {
                const maxValue = dataPoints.reduce(
                    (max, current) => (max && current && max > current ? max : current),
                    dataPoints[0]
                );

                return maxValue + maxValue / 10;
            };

            const labels = data.map((item) => {
                const date = new Date(item.average_date);

                return `${strings.months[date.getMonth()]} ${date.getFullYear()}`;
            });
            const myChart = new ChartJsImage();
            myChart.setConfig({
                type: 'line',
                options: { ...defaultOptionsLine(getMaxValueYaxis()) },
                data: {
                    labels: labels,
                    datasets: [
                        {
                            data: dataPoints,
                            ...defaultFillOptions,
                        },
                    ],
                },
            });
            myChart
                .setWidth(1024)
                .setHeight(500)
                .toDataUrl()
                .then((data) => setPriceImage(data));
        }
    }, [project.indexData]);

    function MyDocument() {
        return (
            <Pdf
                {...{
                    reportSettings: { showRefPrice: show_ref_price },
                    project: { ...project, reportComment: comment },
                    marketData,
                    refData,
                    selectedReportItems: selectedReportItems,
                    soldMarkerObjects,
                    currentCoordinates,
                    hideClientData: type === LOADING_TYPE.CLIENT,
                    userData: userData,
                    priceImage,
                    marketImage,
                    adsCalculations,
                    soldCalculations,
                }}
            />
        )
    }
    const GenerateAndOpenPDF = async () => {
        setLoaded(true);
        const blob = await pdf(<MyDocument />).toBlob();
        const url = URL.createObjectURL(blob);
        if (pdfContainerRef.current) {
            pdfContainerRef.current.src = url;
        } else {
            setTimeout(() => {
                GenerateAndOpenPDF();
            }, 1000);
        }
    };

    // Only generate PDF when all necessary data is loaded
    const marketDataReady = !market || (marketSuccess && !!marketImage);
    const priceDevDataReady = !priceDevelopment || (priceDevelopmentLoaded && indexValuesReady && project.indexData && priceImage);
    const adsDataReady = !ads || (project.adsTableData && isAdsFetchingDone);
    const soldDataReady = !sold || (project.soldTableData && isSoldFetchingDone);
    const userDataReady = !isUserLoading && !isUserError && userData;

    useEffect(() => {
        if (adsLoaded && salesLoaded && marketDataReady && priceDevDataReady && adsDataReady && soldDataReady && userDataReady && !loaded) {
            GenerateAndOpenPDF();
        }
    }, [adsLoaded, salesLoaded, marketDataReady, priceDevDataReady, adsDataReady, soldDataReady, userDataReady, loaded]);
    return loaded && (
        <iframe
            ref={pdfContainerRef}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ position: "absolute" }}
        />
    )
}